import { useTranslation } from 'react-i18next';

import { minus, plus } from '@amzn/storm-ui-icons';

import { CustomIconButton } from '../../../../../UiComponentsLibrary/CustomIconButton';
import { CustomIconButtonColor } from '../../../../../UiComponentsLibrary/CustomIconButton/constants/CustomIconButtonColor';
import { SOV_CONTEXTUAL_NODE_FIELDS } from '../../constants/DetailedBookingConstants';
import {
  DetailedBookingSOVData,
  SOVContextualNodeData,
  SOVContextualNodeErrors,
} from '../../interface/DetailedBookingInterface';
import { reCalculateValueBlockedByLineItem } from '../../services/sovFormHelpers/reCalculateValueBlockedByLineItem';
import {
  ContextualNodeContainer,
  ContextualNodeHeader,
  ContextualNodeRow,
  ContextualNodeTitle,
  RemoveButtonContainer,
} from '../CreateBookingForm/CreateDetailedBookingForm.styles';
import ContextualNodeField, {
  PartialContextualFieldProps,
} from './ContextualNodeFieldSOV';

interface ContextualNodesSectionProps {
  contextualNodes: SOVContextualNodeData[];
  setContextualNodes: React.Dispatch<
    React.SetStateAction<SOVContextualNodeData[]>
  >;
  setContextualNodeErrors: React.Dispatch<
    React.SetStateAction<SOVContextualNodeErrors[]>
  >;
  propsToReCalculatelineItemValue: {
    typeOfPurchase: string;
    isContextualSelected: boolean;
    setFormData: React.Dispatch<React.SetStateAction<DetailedBookingSOVData>>;
  };
  partialContextualNodeFieldProps: PartialContextualFieldProps;
}

const contextualNodeRow = (
  node: SOVContextualNodeData,
  removeContextualNode: (nodeId: string) => void,
  contextualNodes: SOVContextualNodeData[],
  partialContextualNodeFieldProps: PartialContextualFieldProps,
  t: (key: string) => string
) => {
  return (
    <ContextualNodeContainer key={node.id}>
      <ContextualNodeRow>
        <RemoveButtonContainer>
          <CustomIconButton
            icon={minus}
            label={t('sov-remove-contextual-node-label')}
            backgroundColor={CustomIconButtonColor.minus}
            handleOnClickCb={() => removeContextualNode(node.id)}
            disabled={contextualNodes.length <= 1}
          />
        </RemoveButtonContainer>
        {SOV_CONTEXTUAL_NODE_FIELDS.map((fieldConfig) => {
          const contextualNodeFieldProps = {
            node,
            fieldConfig,
            ...partialContextualNodeFieldProps,
          };
          return (
            <ContextualNodeField
              key={fieldConfig.key}
              {...contextualNodeFieldProps}
            />
          );
        })}
      </ContextualNodeRow>
    </ContextualNodeContainer>
  );
};

const ContextualNodesSectionSOV = ({
  contextualNodes,
  setContextualNodes,
  setContextualNodeErrors,
  propsToReCalculatelineItemValue,
  partialContextualNodeFieldProps,
}: ContextualNodesSectionProps) => {
  const { t } = useTranslation();
  const reCalculatedLineItemValue = (updatedNodes: SOVContextualNodeData[]) =>
    reCalculateValueBlockedByLineItem({
      nodes: updatedNodes,
      ...propsToReCalculatelineItemValue,
    });

  const addContextualNode = () => {
    const newNode: SOVContextualNodeData = {
      id: Date.now().toString(),
      contextualNode: '',
      sovPercentageForContextualGP: 0,
      impressionBooked: 0,
      valueBooked: 0,
    };
    const updatedNodes = [...contextualNodes, newNode];
    setContextualNodes(updatedNodes);
    reCalculatedLineItemValue(updatedNodes);
  };

  const removeContextualNode = (nodeId: string) => {
    if (contextualNodes.length > 1) {
      const nodeIndex = contextualNodes.findIndex((n) => n.id === nodeId);
      const updatedNodes = contextualNodes.filter((node) => node.id !== nodeId);
      setContextualNodes(updatedNodes);
      setContextualNodeErrors((prev) =>
        prev.filter((_, index) => index !== nodeIndex)
      );
      reCalculatedLineItemValue(updatedNodes);
    }
  };

  // Calculate max nodes allowed based on selected package
  const maxNodesAllowed =
    partialContextualNodeFieldProps.slotPackagesData.find(
      (slotPackage) =>
        slotPackage.slotPackageName ===
        partialContextualNodeFieldProps.formData.gpSlot
    )?.segments.length || 0;

  const isAddButtonDisabled =
    !partialContextualNodeFieldProps.formData.gpSlot ||
    contextualNodes.length === maxNodesAllowed; // When the nodes max out the number of possible nodes, + button is disabled

  return (
    <>
      <ContextualNodeHeader id="sov-contextual-node-header">
        <ContextualNodeTitle>
          {t('sov-contextual-node-title')}
        </ContextualNodeTitle>
        <CustomIconButton
          icon={plus}
          label={t('sov-add-contextual-node-label')}
          backgroundColor={CustomIconButtonColor.plus}
          handleOnClickCb={addContextualNode}
          disabled={isAddButtonDisabled}
        />
      </ContextualNodeHeader>
      {contextualNodes.map((node) =>
        contextualNodeRow(
          node,
          removeContextualNode,
          contextualNodes,
          partialContextualNodeFieldProps,
          t
        )
      )}
    </>
  );
};

export default ContextualNodesSectionSOV;
